import { createEffect, createMemo, createSignal, on, onCleanup, onMount, Show, type JSX } from 'solid-js';
import { normalizedColor, percent } from '../lib/format';
import { GlowSpriteCache } from '../lib/glowLayer';
import { instrumented } from '../lib/instrumentation';
import {
  clampLevel,
  contiguousSelections,
  nearestProjected,
  type PanelElement,
  type PanelModel,
  type PanelRun,
  type ProjectedPoint,
  type SchematicLayout,
} from '../lib/panelElements';
import type { FixtureValues } from '../lib/types';
import { TEST_IDS } from '../testIds';

interface CanvasTransform { offsetX: number; offsetY: number; scale: number }
interface View2D { zoom: number; panX: number; panY: number }
type DragKind = 'marquee' | 'pan';
interface CanvasDrag {
  pointerId: number;
  canvas: HTMLCanvasElement;
  kind: DragKind;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  lastX: number;
  lastY: number;
  startKey?: string;
  additive: boolean;
  moved: boolean;
  baseSelection: Set<string>;
}
interface MarqueeRect { left: number; top: number; width: number; height: number }
/**
 * What one element needs to be drawn that depends only on the model and the fixture state: its fill and,
 * for the discrete tape cell, the world-space span to its run neighbour (undefined when it has none).
 */
interface ElementLook { fill: string; span?: { length: number; angle: number } }
interface HoverState { element: PanelElement; x: number; y: number; block: Set<string> }

export interface RoomCanvasHandle { resetView: () => void }
export interface RoomCanvasProps {
  model?: PanelModel;
  levels: ReadonlyMap<string, number>;
  unknownKeys?: ReadonlySet<string>;
  fixtures?: ReadonlyMap<string, FixtureValues>;
  interactive?: boolean;
  selectedKeys?: ReadonlySet<string>;
  onSelectionChange?: (selection: Set<string>) => void;
  blockAt?: (key: string, model: PanelModel) => Set<string>;
  storageKey?: string;
  class?: string;
  testId?: string;
  ariaLabel?: string;
  mirrorPanelRevision?: string | number;
  apiRef?: (api: RoomCanvasHandle) => void;
  fitLayoutAspect?: boolean;
}

const DEFAULT_VIEW: View2D = { zoom: 1, panX: 0, panY: 0 };
const MIN_ZOOM = .25;
const MAX_ZOOM = 20;
const LABEL_MIN_CELL = 14;
const LABEL_FULL_CELL = 22;
const ACCENT = '#62d1c9';

function extents(layout: SchematicLayout): { minX: number; minY: number; maxX: number; maxY: number } {
  if (layout.bounds) return { minX: layout.bounds.min[0], minY: layout.bounds.min[1], maxX: layout.bounds.max[0], maxY: layout.bounds.max[1] };
  const points = layout.elements.map((element) => element.point);
  return {
    minX: Math.min(...points.map((point) => point[0])), minY: Math.min(...points.map((point) => point[1])),
    maxX: Math.max(...points.map((point) => point[0])), maxY: Math.max(...points.map((point) => point[1])),
  };
}

function marqueeRect(drag: CanvasDrag): MarqueeRect {
  return {
    left: Math.min(drag.startX, drag.currentX), top: Math.min(drag.startY, drag.currentY),
    width: Math.abs(drag.currentX - drag.startX), height: Math.abs(drag.currentY - drag.startY),
  };
}

function readView(key: string | undefined): View2D {
  if (!key) return { ...DEFAULT_VIEW };
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? 'null') as Partial<View2D> | null;
    if (value && Number.isFinite(value.zoom) && Number.isFinite(value.panX) && Number.isFinite(value.panY)) return value as View2D;
  } catch { /* private browsing can deny storage */ }
  return { ...DEFAULT_VIEW };
}

function writeView(key: string | undefined, value: View2D): void {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage is optional */ }
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  context.moveTo(x + r, y); context.lineTo(x + width - r, y);
  context.quadraticCurveTo(x + width, y, x + width, y + r);
  context.lineTo(x + width, y + height - r);
  context.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  context.lineTo(x + r, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - r);
  context.lineTo(x, y + r); context.quadraticCurveTo(x, y, x + r, y);
}

export function RoomCanvas(props: RoomCanvasProps): JSX.Element {
  const [view, setView] = createSignal<View2D>(readView(props.storageKey));
  const [drag, setDrag] = createSignal<CanvasDrag>();
  const [hover, setHover] = createSignal<HoverState>();
  const [spaceHeld, setSpaceHeld] = createSignal(false);
  const glow = new GlowSpriteCache();
  const touches = new Map<number, { x: number; y: number }>();
  let canvas!: HTMLCanvasElement;
  let host!: HTMLDivElement;
  let observer: ResizeObserver | undefined;
  let transform: CanvasTransform = { offsetX: 0, offsetY: 0, scale: 1 };
  let pinchDistance = 0;
  let pinchMidX = 0;
  let pinchMidY = 0;
  let pointerInside = false;

  const selected = () => props.selectedKeys ?? new Set<string>();
  const activeMarquee = createMemo(() => {
    const value = drag();
    return value?.kind === 'marquee' && value.moved ? marqueeRect(value) : undefined;
  });
  /**
   * Per-element lookups (run neighbour, fixture element state, tint string), indexed like
   * `model.elements`. Computed once per model or fixtures change, never per drawn frame: a live view
   * repaints many times a second with only the levels changing.
   */
  const elementLooks = createMemo((): ElementLook[] => {
    const value = props.model;
    if (!value) return [];
    const fixtures = props.fixtures;
    const runs = new Map<string, PanelRun>();
    for (const run of value.runs) if (!runs.has(run.id)) runs.set(run.id, run);
    return value.elements.map((element) => {
      const fixture = element.deviceId ? fixtures?.get(element.deviceId) : undefined;
      const tint = fixture?.elements?.find((candidate) => candidate.index === element.elementIndex)?.colorDisplay
        ?? fixture?.colorDisplay;
      const fill = tint ? normalizedColor(tint) : '#f4e7c2';
      const run = runs.get(element.runId);
      const neighbour = run?.elements[element.runElementIndex + 1] ?? run?.elements[element.runElementIndex - 1];
      if (!neighbour) return { fill };
      const dx = neighbour.point[0] - element.point[0];
      const dy = neighbour.point[1] - element.point[1];
      return { fill, span: { length: Math.hypot(dx, dy), angle: Math.atan2(dy, dx) } };
    });
  });
  const aspectRatio = createMemo(() => {
    if (!props.fitLayoutAspect || !props.model) return undefined;
    const bounds = extents(props.model.layout);
    return `${Math.max(.001, bounds.maxX - bounds.minX)} / ${Math.max(.001, bounds.maxY - bounds.minY)}`;
  });

  createEffect(() => {
    const key = props.storageKey;
    setView(readView(key));
  });
  createEffect(() => writeView(props.storageKey, view()));

  function baseFit(value: PanelModel, rect: DOMRect) {
    const bounds = extents(value.layout);
    const margin = value.elements.length > 100 ? 30 : 54;
    const width = Math.max(.001, bounds.maxX - bounds.minX);
    const height = Math.max(.001, bounds.maxY - bounds.minY);
    return { scale: Math.max(.001, Math.min((rect.width - margin * 2) / width, (rect.height - margin * 2) / height)), bounds };
  }

  function computeTransform(value: PanelModel, rect: DOMRect, viewport: View2D): CanvasTransform {
    const { scale: fit, bounds } = baseFit(value, rect);
    const scale = fit * viewport.zoom;
    const width = Math.max(.001, bounds.maxX - bounds.minX);
    const height = Math.max(.001, bounds.maxY - bounds.minY);
    return {
      offsetX: (rect.width - width * scale) / 2 - bounds.minX * scale + viewport.panX,
      offsetY: (rect.height - height * scale) / 2 - bounds.minY * scale + viewport.panY,
      scale,
    };
  }

  function projected(element: PanelElement): ProjectedPoint {
    return { key: element.key, x: transform.offsetX + element.point[0] * transform.scale, y: transform.offsetY + element.point[1] * transform.scale, visible: true };
  }

  function screenPixels(): ProjectedPoint[] { return props.model?.elements.map(projected) ?? []; }

  function drawSelectionOutline(context: CanvasRenderingContext2D, run: PanelRun, selection: ReadonlySet<string>, radius: number, stroke: string): void {
    const pad = Math.max(2.5, radius + 2.5);
    const pieces = contiguousSelections(selection, run).flatMap((stretch) => {
      const straight: PanelElement[][] = [];
      for (const element of stretch) {
        const last = straight.at(-1);
        if (last && last[0].dataRunId === element.dataRunId) last.push(element);
        else straight.push([element]);
      }
      return straight;
    });
    for (const stretch of pieces) {
      const points = stretch.map(projected);
      context.strokeStyle = stroke;
      context.lineWidth = 1.25;
      if (points.length === 1) {
        roundedRect(context, points[0].x - pad, points[0].y - pad, pad * 2, pad * 2, pad * .6);
      } else {
        const xs = points.map((point) => point.x);
        const ys = points.map((point) => point.y);
        roundedRect(context, Math.min(...xs) - pad, Math.min(...ys) - pad,
          Math.max(...xs) - Math.min(...xs) + pad * 2, Math.max(...ys) - Math.min(...ys) + pad * 2, Math.min(6, pad));
      }
      context.stroke();
    }
  }

  function medianCellWidth(value: PanelModel, scale: number): number {
    const samples: number[] = [];
    for (const run of value.runs) {
      if (run.elements.length < 2) continue;
      const step = Math.max(1, Math.floor(run.elements.length / 8));
      for (let index = step; index < run.elements.length; index += step) {
        const left = run.elements[index - 1].point;
        const right = run.elements[index].point;
        samples.push(Math.hypot(right[0] - left[0], right[1] - left[1]) * scale);
      }
    }
    samples.sort((left, right) => left - right);
    return samples.length ? samples[Math.floor(samples.length / 2)] : 0;
  }

  function draw(): void {
    const value = props.model;
    if (!value?.elements.length || !canvas || !host) return;
    const rect = host.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    // Assigning a canvas dimension reallocates and clears its backing store even when the value is
    // unchanged, so the store is resized only when the size or the device pixel ratio moved it.
    const backingWidth = Math.max(1, Math.round(rect.width * ratio));
    const backingHeight = Math.max(1, Math.round(rect.height * ratio));
    if (canvas.width !== backingWidth) canvas.width = backingWidth;
    if (canvas.height !== backingHeight) canvas.height = backingHeight;
    const cssWidth = `${rect.width}px`;
    const cssHeight = `${rect.height}px`;
    if (canvas.style.width !== cssWidth) canvas.style.width = cssWidth;
    if (canvas.style.height !== cssHeight) canvas.style.height = cssHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    // The kept backing store also keeps its context state; save/restore returns it to the defaults.
    context.save();
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    transform = computeTransform(value, rect, view());
    const scale = transform.scale;
    const project = ([x, y]: [number, number]) => [transform.offsetX + x * scale, transform.offsetY + y * scale] as const;
    const levels = props.levels;
    const looks = elementLooks();
    const selection = selected();
    const dense = value.elements.length > 1600;
    const tapeWidth = Math.max(2.2, Math.min(18, scale * .035));
    const radius = dense ? Math.max(1.2, Math.min(9, scale * .016)) : value.elements.length > 100 ? Math.max(2.4, Math.min(9, scale * .02)) : 6;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    for (const run of value.runs) for (const segment of run.segments) {
      context.beginPath();
      segment.points.forEach((raw, index) => {
        const [x, y] = project(raw);
        if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
      });
      context.strokeStyle = 'rgb(147 170 165 / 16%)';
      context.lineWidth = tapeWidth;
      context.stroke();
    }
    const bleed = 80;
    // Indices into `value.elements` (and `looks`) of the elements on screen, with their projected points.
    const onScreen: number[] = [];
    const screenX: number[] = [];
    const screenY: number[] = [];
    value.elements.forEach((element, index) => {
      const x = transform.offsetX + element.point[0] * scale;
      const y = transform.offsetY + element.point[1] * scale;
      if (x < -bleed || x > rect.width + bleed || y < -bleed || y > rect.height + bleed) return;
      onScreen.push(index); screenX.push(x); screenY.push(y);
    });
    const cellWidth = medianCellWidth(value, scale);
    const discrete = cellWidth >= 7;
    const labelAlpha = cellWidth <= LABEL_MIN_CELL ? 0 : Math.min(1, (cellWidth - LABEL_MIN_CELL) / (LABEL_FULL_CELL - LABEL_MIN_CELL));
    context.save();
    context.globalCompositeOperation = 'lighter';
    const unknownKeys = props.unknownKeys;
    for (let at = 0; at < onScreen.length; at += 1) {
      const element = value.elements[onScreen[at]];
      if (element.unresolved || element.awaitingRig || unknownKeys?.has(element.key)) continue;
      const sprite = glow.spriteFor(levels.get(element.key) ?? 0, radius);
      if (!sprite) continue;
      context.drawImage(sprite.canvas, screenX[at] - sprite.radius, screenY[at] - sprite.radius);
    }
    context.restore();
    for (let at = 0; at < onScreen.length; at += 1) {
      const element = value.elements[onScreen[at]];
      const look = looks[onScreen[at]];
      const unknown = element.unresolved || element.awaitingRig || (unknownKeys?.has(element.key) ?? false);
      const level = clampLevel(levels.get(element.key) ?? 0);
      const x = screenX[at];
      const y = screenY[at];
      context.globalAlpha = unknown ? .55 : .1 + level * .9;
      context.fillStyle = unknown ? '#8b9b98' : look.fill;
      if (discrete) {
        const span = look.span
          ? { length: Math.max(3, look.span.length * scale), angle: look.span.angle }
          : { length: Math.max(3, scale * .02), angle: 0 };
        context.save(); context.translate(x, y); context.rotate(span.angle);
        const length = Math.max(2, span.length - Math.max(1, span.length * .16));
        roundedRect(context, -length / 2, -tapeWidth * .38, length, tapeWidth * .76, Math.min(2.5, tapeWidth * .3));
        context.fill(); context.restore();
      } else {
        context.beginPath(); context.arc(x, y, radius, 0, Math.PI * 2); context.fill();
      }
    }
    context.globalAlpha = 1;
    if (labelAlpha > 0) {
      context.save(); context.globalAlpha = labelAlpha;
      context.font = `600 ${Math.min(13, Math.max(8, cellWidth * .52))}px ui-sans-serif, system-ui, sans-serif`;
      context.textAlign = 'center'; context.textBaseline = 'middle'; context.fillStyle = 'rgb(8 18 22 / 78%)';
      for (let at = 0; at < onScreen.length; at += 1) {
        context.fillText(String(value.elements[onScreen[at]].runElementIndex + 1), screenX[at], screenY[at] + 1);
      }
      context.restore(); canvas.dataset.pixelLabels = String(onScreen.length);
    } else canvas.dataset.pixelLabels = '0';
    if (props.interactive) {
      const hovered = hover();
      if (hovered?.block.size && !selection.has(hovered.element.key)) {
        for (const run of value.runs) drawSelectionOutline(context, run, hovered.block, radius, 'rgb(184 244 237 / 55%)');
      }
      for (const run of value.runs) drawSelectionOutline(context, run, selection, radius, ACCENT);
      if (hovered && !hovered.block.size) {
        const { x, y } = projected(hovered.element);
        context.beginPath(); context.arc(x, y, radius + 3.5, 0, Math.PI * 2);
        context.strokeStyle = '#b8f4ed'; context.lineWidth = 1.25; context.stroke();
      }
    }
    context.restore();
    if (instrumented) {
      canvas.dataset.elements = JSON.stringify(value.elements.map((element) => {
        const point = projected(element);
        return [element.key, element.runId, element.dataRunId, Math.round(point.x * 10) / 10, Math.round(point.y * 10) / 10];
      }));
      // An element with no level in the map has none to publish: it is left out, never written as 0.
      canvas.dataset.levels = JSON.stringify(Object.fromEntries(value.elements.flatMap((element): Array<[string, number | null]> => {
        if (props.unknownKeys?.has(element.key)) return [[element.key, null]];
        const level = levels.get(element.key);
        return level === undefined ? [] : [[element.key, Math.round(level * 1_000) / 1_000]];
      })));
    }
    canvas.dataset.offsetX = String(transform.offsetX); canvas.dataset.offsetY = String(transform.offsetY);
    canvas.dataset.scale = String(transform.scale); canvas.dataset.zoom = view().zoom.toFixed(4);
    canvas.dataset.panX = view().panX.toFixed(2); canvas.dataset.panY = view().panY.toFixed(2);
    canvas.dataset.cellWidth = cellWidth.toFixed(2); canvas.dataset.selection = JSON.stringify([...selection]);
    canvas.dataset.elementCount = String(value.elements.length); canvas.dataset.visibleCount = String(onScreen.length);
    canvas.dataset.runCount = String(value.runs.length); canvas.dataset.layoutSchema = value.layout.schema;
  }

  createEffect(on(
    () => [props.model, props.levels, props.unknownKeys, props.fixtures, props.selectedKeys, hover(), view()] as const,
    () => queueMicrotask(draw),
  ));

  function observeHost(element: HTMLDivElement): void {
    host = element;
    observer?.disconnect();
    observer = new ResizeObserver(draw);
    observer.observe(element);
    queueMicrotask(draw);
  }

  function zoomAbout(x: number, y: number, factor: number): void {
    const value = props.model;
    if (!value || !host) return;
    const rect = host.getBoundingClientRect();
    setView((current) => {
      const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, current.zoom * factor));
      if (zoom === current.zoom) return current;
      const before = computeTransform(value, rect, current);
      const worldX = (x - before.offsetX) / before.scale;
      const worldY = (y - before.offsetY) / before.scale;
      const after = computeTransform(value, rect, { zoom, panX: 0, panY: 0 });
      return { zoom, panX: x - (after.offsetX + worldX * after.scale), panY: y - (after.offsetY + worldY * after.scale) };
    });
  }
  const panBy = (x: number, y: number) => setView((current) => ({ ...current, panX: current.panX + x, panY: current.panY + y }));
  const resetView = () => setView({ ...DEFAULT_VIEW });

  function localPoint(event: PointerEvent | WheelEvent): [number, number] {
    const bounds = (event.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    return [event.clientX - bounds.left, event.clientY - bounds.top];
  }
  function closestElement(x: number, y: number, pointerType: string): PanelElement | undefined {
    const value = props.model;
    if (!value) return undefined;
    const nearest = nearestProjected(screenPixels(), x, y, pointerType === 'touch' ? 26 : 12);
    return nearest ? value.byKey.get(nearest.key) : undefined;
  }
  function replaceSelection(update: (current: ReadonlySet<string>) => Set<string>): void {
    props.onSelectionChange?.(update(selected()));
  }
  function toggleElement(key: string, additive: boolean): void {
    const value = props.model;
    const block = value ? props.blockAt?.(key, value) ?? new Set<string>() : new Set<string>();
    const target = block.size ? block : new Set([key]);
    replaceSelection((current) => {
      if (additive) {
        const next = new Set(current);
        const all = [...target].every((candidate) => next.has(candidate));
        for (const candidate of target) if (all) next.delete(candidate); else next.add(candidate);
        return next;
      }
      return current.size === target.size && [...target].every((candidate) => current.has(candidate)) ? new Set<string>() : target;
    });
  }
  function selectMarquee(rect: MarqueeRect, additive: boolean, base: ReadonlySet<string>): void {
    const inside = new Set<string>();
    for (const pixel of screenPixels()) if (pixel.x >= rect.left && pixel.x <= rect.left + rect.width && pixel.y >= rect.top && pixel.y <= rect.top + rect.height) inside.add(pixel.key);
    props.onSelectionChange?.(additive ? new Set([...base, ...inside]) : inside);
  }
  function pointerDown(event: PointerEvent): void {
    if (!props.interactive || (event.button !== 0 && event.button !== 1 && event.button !== 2)) return;
    event.preventDefault();
    const target = event.currentTarget as HTMLCanvasElement;
    const [x, y] = localPoint(event);
    pointerInside = true;
    if (event.pointerType === 'touch') {
      touches.set(event.pointerId, { x, y });
      if (touches.size === 2) {
        const points = [...touches.values()];
        pinchDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        pinchMidX = (points[0].x + points[1].x) / 2; pinchMidY = (points[0].y + points[1].y) / 2;
        setDrag(); return;
      }
    }
    const hit = closestElement(x, y, event.pointerType);
    target.setPointerCapture(event.pointerId); setHover();
    const kind: DragKind = event.button === 1 || event.button === 2 || (spaceHeld() && event.pointerType !== 'touch') ? 'pan' : 'marquee';
    setDrag({ pointerId: event.pointerId, canvas: target, kind, startX: x, startY: y, currentX: x, currentY: y,
      lastX: x, lastY: y, startKey: hit?.key, additive: event.shiftKey, moved: false,
      baseSelection: event.shiftKey ? new Set(selected()) : new Set<string>() });
  }
  function pointerMove(event: PointerEvent): void {
    if (!props.interactive) return;
    const [x, y] = localPoint(event);
    pointerInside = true;
    if (event.pointerType === 'touch' && touches.has(event.pointerId)) {
      touches.set(event.pointerId, { x, y });
      if (touches.size === 2) {
        event.preventDefault();
        const points = [...touches.values()];
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const midX = (points[0].x + points[1].x) / 2; const midY = (points[0].y + points[1].y) / 2;
        if (pinchDistance > 0) panBy(midX - pinchMidX, midY - pinchMidY);
        if (pinchDistance > 4 && distance > 4) zoomAbout(midX, midY, distance / pinchDistance);
        pinchDistance = distance; pinchMidX = midX; pinchMidY = midY; return;
      }
    }
    const current = drag();
    if (!current || current.pointerId !== event.pointerId) {
      if (event.pointerType === 'mouse') updateHover(x, y, event.pointerType);
      return;
    }
    event.preventDefault();
    const moved = current.moved || Math.hypot(x - current.startX, y - current.startY) >= 4;
    if (current.kind === 'pan') panBy(x - current.lastX, y - current.lastY);
    setDrag({ ...current, currentX: x, currentY: y, lastX: x, lastY: y, moved });
  }
  function finishPointer(event: PointerEvent, cancelled = false): void {
    touches.delete(event.pointerId);
    if (touches.size < 2) { pinchDistance = 0; pinchMidX = 0; pinchMidY = 0; }
    const current = drag();
    if (!current || current.pointerId !== event.pointerId) return;
    const [x, y] = localPoint(event);
    const final = { ...current, currentX: x, currentY: y };
    if (!cancelled) {
      if (final.kind === 'marquee' && final.moved) selectMarquee(marqueeRect(final), final.additive, final.baseSelection);
      else if (final.kind === 'marquee' && final.startKey) toggleElement(final.startKey, final.additive);
      else if (final.kind === 'marquee' && !final.additive) props.onSelectionChange?.(new Set());
    }
    if (final.canvas.hasPointerCapture(event.pointerId)) final.canvas.releasePointerCapture(event.pointerId);
    setDrag();
  }
  function updateHover(x: number, y: number, pointerType: string): void {
    const value = props.model;
    const element = closestElement(x, y, pointerType);
    setHover(element && value ? { element, x, y, block: props.blockAt?.(element.key, value) ?? new Set() } : undefined);
  }
  function pointerLeave(event: PointerEvent): void {
    touches.delete(event.pointerId); pointerInside = false;
    if (!drag() && event.pointerType === 'mouse') setHover();
  }
  function wheel(event: WheelEvent): void {
    if (!props.interactive) return;
    event.preventDefault();
    const [x, y] = localPoint(event);
    if (event.ctrlKey || event.metaKey) { zoomAbout(x, y, Math.exp(-event.deltaY * .012)); return; }
    const step = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? (host?.clientHeight || 600) : 1;
    panBy(-event.deltaX * step, -event.deltaY * step);
  }
  function doubleClick(event: MouseEvent): void {
    if (!props.interactive) return;
    const bounds = (event.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    zoomAbout(event.clientX - bounds.left, event.clientY - bounds.top, 1.8);
  }

  const typingInto = (target: EventTarget | null) => Boolean((target as HTMLElement | null)?.matches('input:not([type="range"]), textarea, select, [contenteditable="true"]'));
  const keyDown = (event: KeyboardEvent) => {
    if (event.code !== 'Space' || typingInto(event.target)) return;
    setSpaceHeld(true);
    if (pointerInside && !typingInto(document.activeElement)) event.preventDefault();
  };
  const keyUp = (event: KeyboardEvent) => { if (event.code === 'Space') setSpaceHeld(false); };
  const releaseRoom = () => setSpaceHeld(false);
  onMount(() => {
    props.apiRef?.({ resetView });
    if (props.interactive) {
      window.addEventListener('keydown', keyDown); window.addEventListener('keyup', keyUp); window.addEventListener('blur', releaseRoom);
    }
  });
  onCleanup(() => {
    observer?.disconnect(); glow.clear();
    window.removeEventListener('keydown', keyDown); window.removeEventListener('keyup', keyUp); window.removeEventListener('blur', releaseRoom);
  });

  const cursor = createMemo(() => {
    if (!props.interactive) return 'default';
    if (spaceHeld()) return drag()?.kind === 'pan' ? 'grabbing' : 'grab';
    return hover()?.block.size ? 'grab' : 'crosshair';
  });

  return (
    <div
      class={props.class ?? 'panel-room-canvas'}
      ref={observeHost}
      data-testid={props.testId ?? TEST_IDS.roomCanvas}
      data-view="2d"
      style={{ 'aspect-ratio': aspectRatio() }}
    >
      <canvas
        ref={canvas}
        aria-label={props.ariaLabel ?? (props.interactive ? 'Interactive 2D room lighting schematic' : 'Room lighting output preview')}
        style={{ cursor: cursor(), 'touch-action': props.interactive ? 'none' : 'pan-y' }}
        data-mirror-panel-revision={props.mirrorPanelRevision ?? ''}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerLeave={pointerLeave}
        onPointerUp={(event) => finishPointer(event)}
        onPointerCancel={(event) => finishPointer(event, true)}
        onWheel={wheel}
        onDblClick={doubleClick}
        onContextMenu={(event) => { if (props.interactive) event.preventDefault(); }}
      />
      <Show when={props.interactive && activeMarquee()}>
        {(rect) => <div class="selection-marquee" style={{ left: `${rect().left}px`, top: `${rect().top}px`, width: `${rect().width}px`, height: `${rect().height}px` }} />}
      </Show>
      <Show when={props.interactive && hover()}>
        {(state) => (
          <div class="pixel-tooltip" style={{ left: `${state().x}px`, top: `${state().y}px` }} role="tooltip">
            <strong>{state().element.runName} · pixel {state().element.runElementIndex + 1}</strong>
            <span>{percent(props.levels.get(state().element.key) ?? 0)}</span>
            <Show when={state().block.size > 1}><small>{state().block.size.toLocaleString()} lit pixels · click to grab the block</small></Show>
          </div>
        )}
      </Show>
    </div>
  );
}
